import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PrimaryButton } from '../../ui/Button/Buttons';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { selectIsPlatformAdmin } from '../../../store/selectors/authSelectors';
import { PencilAltIcon, PlusIcon, UploadIcon } from '../../../icons/SvgIcons';
import { useNavigate } from 'react-router-dom';
import {
  setEnableStepper,
  setShowPreStep
} from '../../../store/slices/stepperSlice';
import { PATHS } from '../../../constants/Paths.constants';
import ModalBulkImport from '../../ui/Modal/ModalBulkImport/ModalBulkImport';

type AddAISystemActionProps = {
  buttonLabel?: string;
  disabled?: boolean;
  className?: string;
  menuAlign?: string;
};

export function AddAISystemAction({
  buttonLabel = 'Add AI system',
  disabled = false,
  className = '',
  menuAlign
}: AddAISystemActionProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isPlatformAdmin = useAppSelector(selectIsPlatformAdmin);

  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0 });

  const addAISystemItems = [
    {
      label: 'Manual Entry',
      action: () => handleManualEntry(),
      icon: PencilAltIcon()
    },
    {
      label: 'Bulk Import',
      action: () => handleBulkImport(),
      icon: UploadIcon()
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !buttonRef.current?.contains(event.target as Node) &&
        !dropdownMenuRef.current?.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleScroll = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isDropdownOpen]);

  const handleManualEntry = () => {
    dispatch(setShowPreStep(true));
    dispatch(setEnableStepper(true));
    navigate(PATHS.QUESTIONNAIRE);
  };

  const handleBulkImport = () => {
    setIsDropdownOpen(false);
    setShowBulkModal(true);
  };

  const handleDropdownToggle = (
    event?: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (disabled) return;

    const button = event?.currentTarget;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const dropdownWidth = 200;
    const dropdownHeight = 82;
    const dropdownPosition =
      menuAlign === 'center'
        ? rect.left + rect.width / 2 - dropdownWidth / 2
        : rect.right - dropdownWidth;

    setDropdownPosition({
      left: Math.max(8, dropdownPosition),
      top:
        rect.bottom + dropdownHeight + 4 > window.innerHeight
          ? rect.top - dropdownHeight - 4
          : rect.bottom + 4
    });
    setIsDropdownOpen((isOpen) => !isOpen);
  };

  const handleOptionClick = (action: () => void) => {
    setIsDropdownOpen(false);
    action();
  };

  if (!isPlatformAdmin) {
    return (
      <PrimaryButton
        onClick={handleManualEntry}
        size="base"
        disabled={disabled}
        icon={PlusIcon()}
        className={`w-auto ${className}`}
      >
        {buttonLabel}
      </PrimaryButton>
    );
  }

  return (
    <div ref={buttonRef} className={className}>
      <PrimaryButton
        onClick={handleDropdownToggle}
        size="base"
        disabled={disabled}
        icon={PlusIcon()}
        className="w-auto"
      >
        {buttonLabel}
      </PrimaryButton>

      {isDropdownOpen &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] w-[200px] py-1"
            style={{
              left: `${dropdownPosition.left}px`,
              top: `${dropdownPosition.top}px`
            }}
          >
            {addAISystemItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleOptionClick(item.action)}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 font-inter text-sm text-gray-700 first:rounded-t-lg last:rounded-b-lg"
              >
                <span className="flex items-center gap-3">
                  {item.icon}
                  {item.label}
                </span>
              </button>
            ))}
          </div>,
          document.body
        )}

      <ModalBulkImport
        showBulkModal={showBulkModal}
        setShowBulkModal={setShowBulkModal}
      />
    </div>
  );
}
